// University of La Verne: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - La Verne posts no current Common Data Set: its Institutional Research
//    Common Data Set page lists files only through CDS 2014-2015. The
//    first-year counts come from the U.S. Department of Education's IPEDS
//    Admissions survey for Fall 2024, first-time degree-seeking students
//    (College Navigator and the ADM2024 complete data file, unit 117140).
//  - La Verne Traditional Undergraduate Admissions: the Application
//    Deadlines table (Fall 2027), The Application Process (rolling
//    admission, the February 1 freshman priority deadline, requirements,
//    the honors invitation) and the Test-Optional Policy. No decision dates
//    are published; there is no early decision or early action plan.
//    Quirk, flagged and NOT corrected: the deadline table prints the Fall
//    2027 final deadline for traditional undergraduates as "August 6, 2026",
//    a date before the February 1, 2027 priority deadline. The page states
//    it as printed and says so. The table's Athletic Training row is the
//    graduate program and is left out.
//  - La Verne Tuition and Fees: 2026-2027 Undergraduate (La Verne Campus)
//    Tuition and Fees (per semester, times two). La Verne posts no cost of attendance (its
//    financial aid pages have none, checked 2026-10-01), so the table holds
//    the billed tuition and required fees only, and the note says plainly
//    that it is not a full budget.
//  - College Scorecard, school id 117140 (net price by family income), read
//    from the Scorecard API on 2026-10-01. The API has no figure for the
//    three bands above $48,000; they render as not published.
//  - Branding: the KB's colours cite trucolor.net, not a La Verne source,
//    and laverne.edu has no brand page (checked 2026-10-01), so branding is
//    null and the page uses the app's neutral.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=117140#admsns',
        read: '2026-10-01',
    },
    research: {
        label: 'La Verne Institutional Research, Common Data Set',
        url: 'https://www.laverne.edu/institutional-research/reports-studies/common-data-set/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'La Verne Traditional Undergraduate Admissions, Application Deadlines',
        url: 'https://www.laverne.edu/admission/traditional-undergraduate/',
        read: '2026-10-01',
    },
    process: {
        label: 'La Verne Traditional Undergraduate Admissions, The Application Process',
        url: 'https://www.laverne.edu/admission/traditional-undergraduate/admissions-requirements/',
        read: '2026-10-01',
    },
    tests: {
        label: 'La Verne Traditional Undergraduate Admissions, Test-Optional Policy',
        url: 'https://www.laverne.edu/admission/traditional-undergraduate/test-optional-policy/',
        read: '2026-10-01',
    },
    tuition: {
        label: 'La Verne, 2026-2027 Undergraduate (La Verne Campus) Tuition and Fees',
        url: 'https://www.laverne.edu/tuition/2026-2027-undergraduate-la-verne-campus-tuition-and-fees/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?117140-University-of-La-Verne',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-la-verne',
    kbId: 'university_of_la_verne',
    name: 'University of La Verne',
    shortName: 'La Verne',
    location: 'La Verne, CA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // No La Verne-sourced colours (the KB cites trucolor.net); the app's neutral is used.
    branding: null,

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 7110,
        admitted: 5060,
        enrolled: 496,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'La Verne\'s latest posted Common Data Set is for 2014-2015, and it publishes no first-year acceptance rate by college or major; the counts above are the federal IPEDS figures for the whole university.',
        source: 'research',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to La Verne once, through the La Verne Application, the Common App or Coalition on Scoir. One program invites admitted students:',
        rows: [
            { school: 'Global Ideas Honors Program', requirement: 'Admitted freshmen are invited with a high school GPA of 3.5 or above and an SAT of 1240, ACT of 26, TOEFL of 97 or IELTS of 7.0, or a GPA of 3.0 or above and an SAT of 1300, ACT of 29, TOEFL of 97 or IELTS of 8.0. La Verne\'s test-optional page adds that honors applicants need no SAT or ACT, since GPA alone can qualify.', source: 'process' },
        ],
        source: 'process',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'February 1' },
        rows: [
            { plan: 'Priority deadline (freshmen)', deadline: 'February 1, 2027', decision: null, binding: false },
            { plan: 'Final deadline', deadline: 'August 6, 2026, as printed (see the note)', decision: null, binding: false },
        ],
        note: 'La Verne admits on a rolling basis; applying by the priority deadline brings early consideration for merit scholarships. Its deadline table prints the fall 2027 final deadline as August 6, 2026, which is before the priority deadline and reads as a misprint; confirm the date with the Office of Admission. La Verne has no early decision or early action plan and does not publish decision dates. The application fee is $50.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'La Verne has made SAT and ACT scores optional since 2020, and no major requires them. If you send scores, they can only help, never harm, and they can add merit scholarship money. La Verne superscores the SAT, combining your best score from each section. You may send scores before or after the admission decision.',
        source: 'tests',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'process',
    },

    cost: {
        year: '2026-2027',
        partial: true,
        rows: [
            { item: 'Tuition (12 to 18 semester hours)', amount: 50000 },
            { item: 'Wellbeing fee', amount: 600 },
            { item: 'ASULV fee', amount: 400 },
        ],
        total: 51000,
        note: 'Billed tuition and required fees for a full-time undergraduate at the La Verne campus, fall 2026 and spring 2027. This is not a full budget: La Verne posts no cost of attendance, so housing, food, books, transportation and personal costs are not in this total. The health insurance fee is listed as to be determined.',
        source: 'tuition',
        netPrice: {
            average: 20523,
            byIncome: [
                { income: '$0 to $30,000', amount: 20429 },
                { income: '$30,001 to $48,000', amount: 18247 },
                { income: '$48,001 to $75,000', amount: 62637 },
                { income: '$75,001 to $110,000', amount: null },
                { income: 'Over $110,000', amount: null },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. The Department publishes no figure for the $75,001 to $110,000 and over $110,000 bands. Your own price comes from La Verne\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
